import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Button,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Switch,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Menu,
  IconButton,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  ExpandMore,
  Visibility,
  VisibilityOff,
  FilterAlt,
  FilterAltOff,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLanes } from '../store/lane.actions';
import { resetLaneState } from '../store/lane.slice';
import {
  selectLanes,
  selectLanesStatus,
  selectLanesError,
  selectDriveStartTime,
} from '../store/lane.selectors';
import {
  selectedDriveName,
  selectedDriveVideoURL,
  selectAllDrives,
} from '../../drive/store/drive.selectors';
import { fetchDrives } from '../../drive/store/drive.actions';
import { selectedCollectionID } from '../../collection/store/collection.selectors';
import { RequestStatus } from '../../../utils/enums';
import LaneGpsMapComponent from './LaneGpsMapComponent';
import TotalIcon from '@mui/icons-material/Dashboard';
import {
  StatItem,
  StatLabel,
  StatsSection,
  StatValue,
} from '../../../utils/common/styles/commonStyles';
import Breadcrumbs from '../../layout/Breadcrumbs';
import { selectAuthUser } from '../../auth/store/auth.selectors';

const glassStyle = {
  backdropFilter: 'blur(12px)',
  background: 'rgba(255,255,255,0.7)',
  border: '1px solid rgba(255,255,255,0.3)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
};

function LaneMapView() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { driveId } = useParams();

  const lanes = useSelector(selectLanes);
  const status = useSelector(selectLanesStatus);
  const error = useSelector(selectLanesError);
  const driveName = useSelector(selectedDriveName);
  const allDrives = useSelector(selectAllDrives);
  const collectionId = useSelector(selectedCollectionID);
  const driveStartTimeIso = useSelector(selectDriveStartTime); // from backend, exact - preferred over any local guess

  const [lineTypeFilters, setLineTypeFilters] = useState([]);
  const [visible, setVisible] = useState({});
  const [selectedLane, setSelectedLane] = useState(null);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [videoStartTime, setVideoStartTime] = useState(null);
  const [videoTime, setVideoTime] = useState(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const videoRef = useRef(null);

  const parseTimestamp = (value) => {
    if (value == null) return null;
    if (typeof value === 'number') return value;
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  };

  const seekVideoToTime = (timeValue) => {
    if (videoRef.current && timeValue != null && videoStartTime != null) {
      const timestamp = parseTimestamp(timeValue);
      if (timestamp == null) return;
      const timeInSeconds = (timestamp - videoStartTime) / 1000;
      videoRef.current.currentTime = Math.max(0, timeInSeconds);
      if (videoRef.current.paused) {
        videoRef.current.play().catch(() => {});
      }
    }
  };

  const storedRow = localStorage.getItem('selectedDriveRow');
  let row = null;
  try {
    row = storedRow ? JSON.parse(storedRow) : null;
  } catch (e) {
    row = null;
  }

  const user = useSelector(selectAuthUser);

  const currentDriveName = allDrives?.find((d) => d.id === driveId)?.name || driveName || row?.name;

  const driveVideoURL = useSelector(selectedDriveVideoURL) || row?.video_url;
  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Collections', path: '/collections' },
    { label: 'Drives', path: '/drives' },
    { label: 'Lane View', path: `/lane-map/${driveId}` },
  ];
  const currentRoleId = Number(
    user?.roleID ||
      user?.roleId ||
      user?.roleid ||
      JSON.parse(localStorage.getItem('user') || '{}')?.roleid
  );
  const shouldShowBreadcrumbs = [1, 2, 3].includes(currentRoleId);

  useEffect(() => {
    if (!driveId) return navigate('/drives');
    dispatch(fetchLanes(driveId));
    if (collectionId && (!allDrives || allDrives.length === 0)) {
      dispatch(fetchDrives(collectionId));
    }
    return () => dispatch(resetLaneState());
  }, [driveId, collectionId]);

  // The backend now returns the drive's exact start time alongside the lanes,
  // so we no longer need to scan every line's GPS timestamps to guess a zero point.
  useEffect(() => {
    const fromApi = parseTimestamp(driveStartTimeIso);
    if (fromApi != null) {
      setVideoStartTime(fromApi);
      return;
    }

    // Fallback for the moment before the lanes fetch resolves: use whatever
    // drive record is already available locally.
    const currentDrive = allDrives?.find((d) => d.id === driveId) || row || null;
    const driveStart = parseTimestamp(currentDrive?.start_time);
    if (driveStart != null) {
      setVideoStartTime(driveStart);
    }
  }, [driveStartTimeIso, allDrives, driveId]);

  const toggleLine = (key) => {
    setVisible((prev) => ({
      ...prev,
      [key]: prev[key] === undefined ? false : !prev[key],
    }));
  };

  const toggleSection = (laneId, section, lines) => {
    const sectionIsChecked = lines.every(
      (line) => visible[`${laneId}_${section}_${line.lineKey}`] !== false
    );
    const updates = {};
    lines.forEach((line) => {
      const key = `${laneId}_${section}_${line.lineKey}`;
      updates[key] = !sectionIsChecked;
    });
    setVisible((prev) => ({ ...prev, ...updates }));
  };

  const showAll = () => {
    const all = {};
    lanes.forEach((lane) => {
      const lines = getLines(lane);
      lines.forEach((l) => {
        all[`${lane.laneid}_${l.section}_${l.lineKey}`] = true;
      });
    });
    setVisible(all);
  };

  const hideAll = () => {
    const all = {};
    lanes.forEach((lane) => {
      const lines = getLines(lane);
      lines.forEach((l) => {
        all[`${lane.laneid}_${l.section}_${l.lineKey}`] = false;
      });
    });
    setVisible(all);
  };

  // Backend shape: lane.lines = { left: FormattedLine[], right: FormattedLine[], aux: FormattedLine[] }
  // No JSON parsing needed anymore - flatten the three arrays into one list, tagging each with its section.
  const getLines = (lane) => {
    const sections = lane?.lines;
    if (!sections) return [];

    const lines = [];
    Object.entries(sections).forEach(([section, sectionLines]) => {
      (sectionLines || []).forEach((line) => {
        lines.push({
          ...line,
          section,
          lineKey: `line_${line.lineId}`,
          pointCount: Array.isArray(line.gpsPoints) ? line.gpsPoints.length : 0,
        });
      });
    });

    return lines;
  };

  const lineTypes = useMemo(() => {
    const types = new Set();
    (lanes || []).forEach((lane) => {
      const lines = getLines(lane);
      lines.forEach((line) => {
        if (line.type) types.add(line.type);
      });
    });
    return Array.from(types).sort();
  }, [lanes]);

  const getFilteredLines = (lane) => {
    const lines = getLines(lane);
    if (lineTypeFilters.length === 0) return lines;
    return lines.filter((l) => lineTypeFilters.includes(l.type));
  };

  const filtered = useMemo(() => {
    return lanes || [];
  }, [lanes]);

  const isFilterActive = lineTypeFilters.length > 0;

  const filteredLanesForMap = useMemo(() => {
    if (!lanes || lineTypeFilters.length === 0) return lanes;

    return lanes.map((lane) => {
      const filteredLines = {};
      Object.entries(lane.lines || {}).forEach(([section, sectionLines]) => {
        const kept = (sectionLines || []).filter((l) => lineTypeFilters.includes(l.type));
        if (kept.length > 0) {
          filteredLines[section] = kept;
        }
      });

      return {
        ...lane,
        lines: filteredLines,
      };
    });
  }, [lanes, lineTypeFilters]);

  const hasLaneData = useMemo(
    () =>
      (filteredLanesForMap || []).some((lane) =>
        getLines(lane).some(
          (line) =>
            line.startGpsPoint ||
            line.endGpsPoint ||
            (Array.isArray(line.gpsPoints) && line.gpsPoints.length > 0)
        )
      ),
    [filteredLanesForMap]
  );

  const stats = useMemo(() => {
    let total = 0,
      visibleCount = 0;
    lanes.forEach((l) => {
      const lines = getFilteredLines(l);
      total += lines.length;
      lines.forEach((line) => {
        const key = `${l.laneid}_${line.section}_${line.lineKey}`;
        if (visible[key] !== false) visibleCount++;
      });
    });
    return { total, visibleCount };
  }, [lanes, visible, lineTypeFilters]);

  if (status === RequestStatus.LOADING) {
    return (
      <Container
        maxWidth={false}
        sx={{
          p: 2,
          height: '90vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          sx={{
            ...glassStyle,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            p: 4,
            minWidth: 300,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" fontWeight="bold">
            Loading Lane Map...
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Fetching lane data and initializing map
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ p: { xs: 1, sm: 2 } }}>
      {/* HEADER */}
      <Paper sx={{ ...glassStyle, p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Box
          display="flex"
          alignItems="center"
          gap={{ xs: 1, sm: 2 }}
          flexDirection={{ xs: 'column', sm: 'row' }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '40%',
              padding: { xs: 0, sm: 0 },
              flexWrap: { xs: 'wrap', md: 'nowrap' },
            }}
          >
            {shouldShowBreadcrumbs && <Breadcrumbs items={breadcrumbItems} />}
          </Box>

          <Box flex={1} display="flex" minWidth={0}>
            <Typography
              variant={{ xs: 'h6' }}
              fontWeight="bold"
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {currentDriveName}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" justifyContent="flex-end">
            <StatsSection>
              <StatItem sx={{ padding: '8px 12px' }}>
                <Box display="flex" alignItems="baseline">
                  <StatValue sx={{ color: 'primary.alt', transition: 'all 0.3s ease' }}>
                    {stats.visibleCount}
                  </StatValue>
                  <StatLabel>Visible</StatLabel>
                </Box>
              </StatItem>

              <StatItem sx={{ padding: '8px 12px' }}>
                <TotalIcon sx={{ fontSize: 20, color: '#667eea', transition: 'all 0.3s ease' }} />
                <Box display="flex" alignItems="baseline">
                  <StatValue sx={{ color: 'primary.alt', transition: 'all 0.3s ease' }}>
                    {stats.total}
                  </StatValue>
                  <StatLabel>Total</StatLabel>
                </Box>
              </StatItem>
            </StatsSection>
          </Box>
        </Box>
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ position: 'relative', overflow: 'hidden', width: '100%' }}>
        {/* OVERLAY SLIDING SIDEBAR */}
        <Box
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            bottom: 12,
            zIndex: 100,
            width: {
              xs: 'min(90vw, 320px)',
              sm: 340,
              md: 380,
            },
            maxWidth: 'calc(100% - 30px)',
            transform: sidebarOpen ? 'translateX(0)' : 'translateX(calc(100% + 20px))',
            transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
            pointerEvents: 'none',
            display: 'flex',
          }}
        >
          {/* Main Panel Content */}
          <Paper
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '16px',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              overflow: 'hidden',
              pointerEvents: sidebarOpen ? 'auto' : 'none',
            }}
          >
            <Box p={2}>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Button
                  fullWidth
                  size="medium"
                  startIcon={isFilterActive ? <FilterAltOff /> : <FilterAlt />}
                  onClick={(e) => {
                    if (isFilterActive) {
                      setLineTypeFilters([]);
                      setFilterMenuAnchor(null);
                      return;
                    }

                    setFilterMenuAnchor(e.currentTarget);
                  }}
                  sx={{
                    color: '#667eea',
                    textTransform: 'none',
                    justifyContent: 'space-between',
                    '& .MuiButton-startIcon': {
                      fontSize: 24,
                    },
                    flex: 1,
                  }}
                >
                  {isFilterActive
                    ? `Clear Filters (${lineTypeFilters.length})`
                    : `Line Type Filters (${lineTypeFilters.length})`}
                </Button>
              </Box>
              <Menu
                anchorEl={filterMenuAnchor}
                open={Boolean(filterMenuAnchor)}
                onClose={() => setFilterMenuAnchor(null)}
                slotProps={{
                  paper: {
                    sx: {
                      maxWidth: 250,
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    p: 1,
                    minWidth: 220,
                    gap: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                  }}
                >
                  {lineTypes.map((type) => (
                    <MenuItem
                      key={type}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        py: 0.5,
                        px: 1,
                        borderRadius: 1,
                        borderBottom: '1px solid #e0e0e0',
                        gap: 1,
                      }}
                    >
                      <Checkbox
                        checked={lineTypeFilters.includes(type)}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setLineTypeFilters((prev) => {
                            if (isChecked) {
                              return prev.includes(type) ? prev : [...prev, type];
                            }
                            return prev.filter((item) => item !== type);
                          });
                        }}
                        size="small"
                        color="success"
                      />
                      <Typography variant="body2">{type}</Typography>
                    </MenuItem>
                  ))}
                </Box>
              </Menu>
            </Box>

            <Box px={2} pb={2} display="flex" gap={1.5} flexWrap={{ xs: 'wrap', md: 'nowrap' }}>
              <Button
                fullWidth
                size="small"
                startIcon={<Visibility />}
                onClick={showAll}
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                  textTransform: 'none',
                  border: '1px solid rgba(255,255,255,0.2)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 12px rgba(102,126,234,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                    boxShadow: '0 6px 20px rgba(102,126,234,0.5)',
                    transform: 'translateY(-2px)',
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                  },
                }}
              >
                Show All
              </Button>
              <Button
                fullWidth
                size="small"
                startIcon={<VisibilityOff />}
                onClick={hideAll}
                variant="outlined"
                sx={{
                  color: '#667eea',
                  borderColor: '#667eea',
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                  textTransform: 'none',
                  border: '2px solid #667eea',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  backgroundColor: 'rgba(102,126,234,0.05)',
                  '&:hover': {
                    backgroundColor: 'rgba(102,126,234,0.15)',
                    borderColor: '#764ba2',
                    color: '#764ba2',
                    boxShadow: '0 4px 12px rgba(102,126,234,0.2)',
                    transform: 'translateY(-2px)',
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                  },
                }}
              >
                Hide All
              </Button>
            </Box>

            <Divider />

            <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
              {status === RequestStatus.SUCCEEDED && !hasLaneData ? (
                <Box sx={{ px: 2, py: 5, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No lane data for this drive
                  </Typography>
                </Box>
              ) : (
                <List dense={{ xs: true, md: false }}>
                  {filtered.map((lane) => {
                    const lines = getFilteredLines(lane);
                    if (lines.length === 0) return null;

                    const grouped = {};
                    lines.forEach((l) => {
                      grouped[l.section] = grouped[l.section] || [];
                      grouped[l.section].push(l);
                    });

                    return (
                      <Box key={lane.laneid}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography variant={{ xs: 'body2', md: 'body1' }}>
                                {`Lane ${lane.laneid}`}
                              </Typography>
                            }
                            secondary={`${lines.length} lines`}
                          />
                        </ListItem>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {Object.entries(grouped).map(([section, lines]) => {
                            const sectionIsChecked = lines.every(
                              (l) => visible[`${lane.laneid}_${section}_${l.lineKey}`] !== false
                            );
                            return (
                              <Box
                                key={section}
                                sx={{
                                  borderBottom: '1px solid #000000',
                                  borderRadius: 1.5,
                                  overflow: 'hidden',
                                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                }}
                              >
                                <Accordion
                                  sx={{
                                    '&.MuiAccordion-root': {
                                      boxShadow: 'none',
                                      '&:before': {
                                        display: 'none',
                                      },
                                    },
                                  }}
                                >
                                  <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography
                                      variant={{ xs: 'caption', md: 'body2' }}
                                      fontWeight={600}
                                    >
                                      {section.toUpperCase()}
                                    </Typography>

                                    <Box ml="auto">
                                      <Switch
                                        checked={sectionIsChecked}
                                        size="small"
                                        onClick={(e) => e.stopPropagation()}
                                        onFocus={(e) => e.stopPropagation()}
                                        onChange={() => {
                                          toggleSection(lane.laneid, section, lines);
                                        }}
                                        sx={{
                                          '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: '#4caf50',
                                          },
                                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                                            {
                                              backgroundColor: '#4caf50',
                                            },
                                        }}
                                      />
                                    </Box>
                                  </AccordionSummary>

                                  <AccordionDetails
                                    sx={{
                                      p: 1,
                                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                    }}
                                  >
                                    <Box
                                      sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                                    >
                                      {lines.map((line) => {
                                        const key = `${lane.laneid}_${section}_${line.lineKey}`;
                                        const isVisible = visible[key] !== false;

                                        return (
                                          <ListItemButton
                                            key={key}
                                            selected={selectedLane?.laneId === lane.laneid}
                                            onClick={() => setSelectedLane(lane)}
                                            dense
                                            sx={{
                                              borderRadius: 1,
                                              border: '1px solid rgba(224, 224, 224, 0.6)',
                                              backgroundColor: isVisible
                                                ? 'rgba(102, 126, 234, 0.08)'
                                                : 'rgba(255, 255, 255, 0.5)',
                                              mb: 0.5,
                                              '&:last-child': {
                                                mb: 0,
                                              },
                                              '&:hover': {
                                                backgroundColor: isVisible
                                                  ? 'rgba(102, 126, 234, 0.15)'
                                                  : 'rgba(102, 126, 234, 0.05)',
                                              },
                                            }}
                                          >
                                            <ListItemText
                                              primary={
                                                <Typography
                                                  variant={{ xs: 'caption', md: 'body2' }}
                                                >
                                                  {line.lineKey}
                                                </Typography>
                                              }
                                              secondary={`Type: ${line.type || 'N/A'}`}
                                            />
                                            <Switch
                                              checked={isVisible}
                                              size="small"
                                              onClick={(e) => e.stopPropagation()}
                                              onFocus={(e) => e.stopPropagation()}
                                              onChange={() => toggleLine(key)}
                                              sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                  color: '#4caf50',
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                                                  {
                                                    backgroundColor: '#4caf50',
                                                  },
                                              }}
                                            />
                                          </ListItemButton>
                                        );
                                      })}
                                    </Box>
                                  </AccordionDetails>
                                </Accordion>
                              </Box>
                            );
                          })}
                        </Box>

                        <Divider />
                      </Box>
                    );
                  })}
                </List>
              )}
            </Box>
          </Paper>

          {/* Floating Sidebar Toggle Button */}
          <IconButton
            onClick={() => setSidebarOpen((isOpen) => !isOpen)}
            aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
            sx={{
              position: 'absolute',
              top: '50%',
              right: '100%',
              transform: 'translateY(-50%)',
              width: 40,
              height: 140,
              border: '1px solid #cbd5e1',
              borderRight: 0,
              borderRadius: '10px 0 0 10px',
              color: '#334155',
              backgroundColor: '#ffffff',
              boxShadow: '-4px 0 10px rgba(15, 23, 42, 0.12)',
              pointerEvents: 'auto',
              p: 0,
              '&:hover': {
                backgroundColor: '#f8fafc',
              },
            }}
          >
            {sidebarOpen ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
          </IconButton>
        </Box>

        <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ width: '100%', margin: 0 }}>
          {/* MAP */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              minWidth: 0,
              width: '100%',
              pl: '0 !important',
              pr: { xs: 0, md: 1 },
              transition: 'all 0.3s ease-in-out',
            }}
          >
            <Paper
              sx={{
                ...glassStyle,
                height: { xs: '50vh', md: '75vh' },
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {status === RequestStatus.SUCCEEDED && !hasLaneData ? (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    minHeight: '400px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#12121a',
                  }}
                >
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    No lane data for this drive
                  </Typography>
                </Box>
              ) : (
                <LaneGpsMapComponent
                  laneGpsData={filteredLanesForMap}
                  visibleLanes={visible}
                  videoStartTime={videoStartTime}
                  videoTime={videoTime}
                  videoPlaying={videoPlaying}
                  onGpsPointClick={seekVideoToTime}
                />
              )}
            </Paper>
          </Grid>

          {/* VIDEO */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              minWidth: 0,
              width: '100%',
              pl: { xs: '0 !important', md: '8px !important' },
              transition: 'all 0.3s ease-in-out',
            }}
          >
            <Paper
              sx={{
                ...glassStyle,
                height: { xs: '50vh', md: '75vh' },
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
                {driveVideoURL ? (
                  // eslint-disable-next-line jsx-a11y/media-has-caption
                  <video
                    ref={videoRef}
                    src={driveVideoURL}
                    controls
                    onTimeUpdate={(event) => setVideoTime(event.currentTarget.currentTime)}
                    onPlay={() => setVideoPlaying(true)}
                    onPause={() => setVideoPlaying(false)}
                    onEnded={() => setVideoPlaying(false)}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      backgroundColor: '#000',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#000',
                      color: '#fff',
                    }}
                  >
                    <Typography>No Video Available</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}

export default LaneMapView;
